import Layout from "../../hocs/Layout";
import { useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import {
  Button,
  Col,
  Row,
  Table,
  Badge,
  Stack,
  Container,
} from "react-bootstrap";
import { connect } from "react-redux";
import { get_contratos, get_contratos_excel } from "../../services/contratos";
import { CiWarning } from "react-icons/ci";
import { FaThLarge, FaList } from "react-icons/fa";
import PaginationComponent from "../../components/pagination/Pagination";
import ContratosFilter from "../../components/contratos/ContratosFilter";
import AgentSelector from '../../components/inputs/AgentSelector';
import UserAutocompleteInput from "../../components/inputs/UserAutoCompleteInput";
import { sectorTypeBadge } from "../../utils/ofertasUtils"; // Keeping for reference if needed
import ContractListCard from "../../components/cards/ContractListCard";

import "./OfertasList.css";

const estados = {
  activa: { descrip: "ACTIVA", bg: "success" },
  baixa: { descrip: "BAJA", bg: "danger" },
  cancelada: { descrip: "CANCELADA", bg: "danger" },
  esborrany: { descrip: "BORRADOR", bg: "primary" },
  impagament: { descrip: "IMPAGA", bg: "danger" },
  modcontractual: { descrip: "MOD. CONTRACTUAL ABIERTA", bg: "danger" },
  pendent: { descrip: "PENDIENTE", bg: "danger" },
  tall: { descrip: "ALTA", bg: "danger" },
  validar: { descrip: "VALIDAR", bg: "danger" },
  saliente: { descrip: "SALIENTE", bg: "danger" },
};

const statusOptions = [
  { value: "activa", label: "Activa" },
  { value: "baixa", label: "Baja" },
  { value: "cancelada", label: "Cancelada" },
  { value: "esborrany", label: "Borrador" },
  { value: "impagament", label: "Impaga" },
  { value: "modcontractual", label: "Mod. Contractual Abierta" },
  { value: "pendent", label: "Pendiente" },
  { value: "tall", label: "Alta" },
  { value: "validar", label: "Validar" },
  { value: "saliente", label: "Saliente" },
];

const ContratosList = ({ user, isAuthenticated }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const resolveAgentParamToUserId = (agentParam) => {
    if (agentParam === 'all') return user?.erp_user_id;
    return agentParam || user?.erp_user_id;
  };

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialFilters = useMemo(() => {
    const f = {};
    const statuses = queryParams.getAll("status").concat(queryParams.getAll("estado"));
    if (statuses.length > 0) {
      f.status = statuses.map(val => statusOptions.find(opt => opt.value === val)).filter(Boolean);
    }

    if (queryParams.get("poliza")) f.poliza = queryParams.get("poliza");
    if (queryParams.get("cliente")) f.cliente = queryParams.get("cliente");
    if (queryParams.get("cups")) f.cups = queryParams.get("cups");
    if (queryParams.get("nif")) f.nif = queryParams.get("nif");
    if (queryParams.get("sector")) f.sector = queryParams.get("sector");
    if (queryParams.get("altaFrom")) f.altaFrom = queryParams.get("altaFrom");
    if (queryParams.get("altaTo")) f.altaTo = queryParams.get("altaTo");
    if (queryParams.get("bajaFrom")) f.bajaFrom = queryParams.get("bajaFrom");
    if (queryParams.get("bajaTo")) f.bajaTo = queryParams.get("bajaTo");

    return f;
  }, [queryParams]);

  const [loading, setLoading] = useState(false);
  const [contratos, setContratos] = useState(false);

  // Initialize from URL
  const [currentPage, setCurrentPage] = useState(() => {
    const p = queryParams.get("page")
    return p ? parseInt(p, 10) : 1;
  });

  const [filters, setFilters] = useState(initialFilters);

  // Agent handling: URL > User Context
  const [selectedAgent, setSelectedAgent] = useState(() => {
    return resolveAgentParamToUserId(queryParams.get("agent"));
  });
  const [agents, setAgents] = useState(() => {
    const agentParam = queryParams.get("agent");
    return agentParam === "all" ? "all" : "";
  });
  const [selectedAgentOption, setSelectedAgentOption] = useState(() => {
    return queryParams.get("agent") || user?.erp_user_id;
  });

  const [selectedUser, setSelectedUser] = useState(null);

  // View mode state with localStorage persistence (default: 'list')
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('contratosViewMode') || 'list';
  });

  const toggleViewMode = () => {
    const newMode = viewMode === 'list' ? 'cards' : 'list';
    setViewMode(newMode);
    localStorage.setItem('contratosViewMode', newMode);
  };

  // Helper to update URL
  const updateUrl = (newParams) => {
    const params = new URLSearchParams(location.search);

    Object.keys(newParams).forEach(key => {
      const value = newParams[key];
      if (value === null || value === undefined || value === "") {
        params.delete(key);
      } else if (Array.isArray(value)) {
        params.delete(key);
        value.forEach(v => params.append(key, v));
      } else {
        params.set(key, value);
      }
    });

    navigate({ search: params.toString() }, { replace: true });
  };

  // Sync State from URL
  useEffect(() => {
    setFilters(initialFilters);

    const pageParam = queryParams.get("page");
    setCurrentPage(pageParam ? parseInt(pageParam, 10) : 1);

    const agentParam = queryParams.get("agent");
    if (agentParam) {
      if (agentParam === 'all') {
        setSelectedAgent(resolveAgentParamToUserId(agentParam));
        setAgents('all');
        setSelectedAgentOption('all');
        setSelectedUser('all'); // Pass string 'all' to UserAutocompleteInput
      } else {
        setSelectedAgent(resolveAgentParamToUserId(agentParam));
        setAgents('');
        setSelectedAgentOption(agentParam);
        setSelectedUser(agentParam); // Pass user ID string to UserAutocompleteInput
      }
    } else {
      // Reset to default user when no agent param
      setSelectedAgent(resolveAgentParamToUserId(null));
      setSelectedUser(null);
    }

  }, [initialFilters, queryParams, user]);


  const getContratos = useCallback(async (page) => {
    const targetUserId = resolveAgentParamToUserId(selectedAgent);
    if (!targetUserId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setContratos(await get_contratos(targetUserId, page, agents, filters));
    setLoading(false);
  }, [selectedAgent, agents, filters, user]);

  useEffect(() => {
    if (isAuthenticated) {
      getContratos(currentPage);
    }
    window.scrollTo(0, 0);
  }, [isAuthenticated, currentPage, getContratos]);

  const handlePageChange = (page) => {
    updateUrl({ page });
  };

  const handleFilter = (newFilters) => {
    const params = {
      page: 1,
      poliza: newFilters.poliza,
      cliente: newFilters.cliente,
      cups: newFilters.cups,
      nif: newFilters.nif,
      sector: newFilters.sector,
      altaFrom: newFilters.altaFrom,
      altaTo: newFilters.altaTo,
      bajaFrom: newFilters.bajaFrom,
      bajaTo: newFilters.bajaTo,
    };

    if (newFilters.status && newFilters.status.length > 0) {
      params.status = newFilters.status.map(s => s.value);
    } else {
      params.status = null;
    }

    updateUrl(params);
  };

  const handleAgentSelect = (agentPrescriptor) => {
    updateUrl({
      agent: agentPrescriptor,
      page: 1
    });
  };


  const handleExportExcel = async () => {
    await get_contratos_excel(selectedAgent, agents, filters);
  };
  const stateBadge = (state, data_baixa) => {
    if (data_baixa && state == 'activa') {
      return (
        <Badge pill bg="warning" text="dark">
          SALIENTE
        </Badge>
      );

    }

    const estado = estados[state] || { descrip: state?.toUpperCase() || "DESCONOCIDO", bg: "secondary" };
    return (
      <Badge pill bg={estado.bg}>
        {estado.descrip}
      </Badge>
    );
  };


  const sectorTypeBadge = (sector_type) => {
    return sector_type === "electrico" ? (
      <Badge pill bg="warning" text="dark">
        ELECTRICIDAD
      </Badge>
    ) : (
      <Badge pill bg="info" text="dark">
        GAS
      </Badge>
    );
  };

  return (
    <Layout>
      <div className="py-5 bg-light min-vh-100">
        <Container style={{ width: "1480px", maxWidth: "100%", margin: "0 auto" }}>
          {(user.is_superadmin || user.is_internal_agent) && (
            <UserAutocompleteInput
              value={selectedUser}
              placeholder="Nombre del usuario..."
              
              delegacionId={user.delegacion}
              isInternalAgent={user.is_internal_agent}
              isSuperAdmin={user.is_superadmin}

              onChange={(selectedOption) => {
                if (selectedOption === 'all') {
                  handleAgentSelect(selectedOption);
                }
                else {
                  setSelectedUser(selectedOption);
                  handleAgentSelect(selectedOption ? selectedOption.value : selectedAgentOption);
                }
              }}
            />
          )}

          {!user.is_superadmin && user.agents && user.agents.length > 0 && (
            <AgentSelector
              agents={user.agents}
              selectedAgent={selectedAgentOption}
              onSelectAgent={handleAgentSelect}
            />
          )}
          <ContratosFilter onFilter={handleFilter} onExport={handleExportExcel} initialFilters={filters} />

          {/* Floating View Toggle Button */}
          <Button
            onClick={toggleViewMode}
            className="position-fixed rounded-circle shadow-lg border-0"
            variant="primary"
            style={{
              bottom: '2rem',
              right: '2rem',
              width: '60px',
              height: '60px',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title={viewMode === 'list' ? 'Vista de tarjetas' : 'Vista de lista'}
          >
            {viewMode === 'list' ? <FaThLarge size={24} /> : <FaList size={24} />}
          </Button>

          {loading ? (
            <></>
          ) : (contratos && contratos.contratos && contratos.contratos.length > 0) ? (
            <>
              {viewMode === 'cards' ? (
                <Row className="mt-4">
                  {contratos.contratos.map((contrato) => (
                    <ContractListCard key={contrato.id} contrato={contrato} />
                  ))}
                </Row>
              ) : (
                <div className="mt-4">
                  <Table hover responsive className="bg-white rounded-4 overflow-hidden shadow-sm">
                    <thead className="bg-light">
                      <tr>
                        <th className="fw-bold text-secondary">Contrato</th>
                        <th className="fw-bold text-secondary">Cliente</th>
                        <th className="fw-bold text-secondary">Sector</th>
                        <th className="fw-bold text-secondary">CUPS</th>
                        <th className="fw-bold text-secondary">Estado</th>
                        <th className="fw-bold text-secondary">Fecha Alta</th>
                        <th className="fw-bold text-secondary">Agente</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contratos.contratos.map((contrato) => (
                        <tr
                          key={contrato.id}
                          onClick={() => {
                            const params = new URLSearchParams(location.search);
                            params.set('sector_type', contrato.sector_type);
                            navigate(`/contratos/contratos/${contrato.id}?${params.toString()}`);
                          }}
                          style={{ cursor: 'pointer' }}
                          className="align-middle"
                        >
                          <td className="fw-bold text-primary">{contrato.name || `#${contrato.id}`}</td>
                          <td>
                            <div className="fw-bold">{contrato.titular || 'N/A'}</div>
                            <small className="text-muted">{contrato.nif || ''}</small>
                          </td>
                          <td>{sectorTypeBadge(contrato.sector_type)}</td>
                          <td className="text-muted">{contrato.cups || 'N/A'}</td>
                          <td>{stateBadge(contrato.state, contrato.data_baixa)}</td>
                          <td className="text-muted">
                            {contrato.data_alta || 'N/A'}
                          </td>
                          <td className="text-muted">
                            {contrato.prescriptor || 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
              <PaginationComponent
                totalPages={contratos.total_pages}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                totalRecords={contratos.total_records}>
              </PaginationComponent>
            </>
          ) : (
            <div className="d-flex flex-column justify-content-center align-items-center mt-5 pt-5 text-center opacity-75">
              <div className="bg-light rounded-circle p-4 mb-3 shadow-sm d-inline-flex">
                <CiWarning className="display-4 text-secondary" />
              </div>
              <h2 className="text-muted fw-bold mb-4">No se han encontrado contratos.</h2>
              <Button
                className="rounded-pill px-5 fw-bold shadow-sm"
                variant="primary"
                onClick={() => getContratos(currentPage)}
              >
                Reintentar
              </Button>
            </div>
          )}
        </Container>
      </div>
    </Layout>
  );
};

const mapStateToProps = (state) => ({
  user: state.Auth.user,
  isAuthenticated: state.Auth.isAuthenticated,
});

export default connect(mapStateToProps)(ContratosList);
